import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Grid,
  Chip,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
} from "@mui/material";
import {
  ArrowBack,
  Save,
  AutoFixHigh,
  Publish,
  Unpublished,
  Refresh,
  Visibility,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import hallTicketService from "../../services/hallTicketService";
import { HALL_TICKET_PERMISSIONS } from "../../utils/hallTicketPermissions";
import PermissionGate from "../../components/common/PermissionGate";

const INSTRUCTION_MAX = 2000;

const CONTENT_FIELDS = [
  { key: "studentName", label: "Student Name" },
  { key: "studentPhoto", label: "Student Photo" },
  { key: "studentId", label: "Student ID" },
  { key: "rollNumber", label: "Roll Number" },
  { key: "seatNumber", label: "Seat Number" },
  { key: "prnNumber", label: "PRN Number" },
];

const BRANDING_FIELDS = [
  { key: "showLogo", label: "Show Logo" },
  { key: "showName", label: "Show Institute Name" },
  { key: "showAddress", label: "Show Address" },
];

// Client-side validation: instruction 0–2000 chars (optional), required center/room.
// Field-named messages surface before submit.
const configValidationSchema = Yup.object({
  instruction: Yup.string()
    .trim()
    .max(INSTRUCTION_MAX, `Instruction must be at most ${INSTRUCTION_MAX} characters`),
  examCenter: Yup.string()
    .trim()
    .required("Exam center is required"),
  examRoom: Yup.string()
    .trim()
    .required("Exam room is required"),
});

const emptyConfig = {
  instruction: "",
  examCenter: "",
  examRoom: "",
  enabledFields: {
    studentName: true,
    studentPhoto: true,
    studentId: true,
    rollNumber: true,
    seatNumber: true,
    // PRN is opt-in: off by default so staff explicitly choose to print it.
    prnNumber: false,
  },
  brandingOptions: {
    showLogo: true,
    showName: true,
    showAddress: true,
  },
};

const parseApiError = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

const publishStateChip = (state) => {
  const value = String(state || "draft").toLowerCase();
  if (value === "published") return { label: "Published", color: "success" };
  if (value === "unpublished") return { label: "Unpublished", color: "warning" };
  return { label: "Draft", color: "default" };
};

const HallTicketManagement = () => {
  const { examId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [publishState, setPublishState] = useState("draft");
  const [enabledFields, setEnabledFields] = useState(emptyConfig.enabledFields);
  const [brandingOptions, setBrandingOptions] = useState(
    emptyConfig.brandingOptions
  );

  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [generateSummary, setGenerateSummary] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);

  const formik = useFormik({
    initialValues: {
      instruction: "",
      examCenter: "",
      examRoom: "",
    },
    validationSchema: configValidationSchema,
    enableReinitialize: false,
    validateOnMount: false,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values) => {
      setSaving(true);
      try {
        const payload = {
          instruction: (values.instruction || "").trim(),
          enabledFields,
          centerAssignment: {
            examCenter: (values.examCenter || "").trim(),
            examRoom: (values.examRoom || "").trim(),
          },
          brandingOptions,
        };
        const res = await hallTicketService.configureHallTicket(examId, payload);
        const config = res?.data?.data ?? res?.data ?? {};
        if (config?.publishState) setPublishState(config.publishState);
        toast.success("Hall ticket configuration saved");
      } catch (err) {
        toast.error(parseApiError(err, "Failed to save configuration"));
      } finally {
        setSaving(false);
      }
    },
  });

  const applyConfig = useCallback(
    (config) => {
      const safe = config || {};
      const center = safe.centerAssignment || {};
      formik.resetForm({
        values: {
          instruction: safe.instruction || "",
          examCenter: center.examCenter || "",
          examRoom: center.examRoom || "",
        },
      });
      setEnabledFields({
        ...emptyConfig.enabledFields,
        ...(safe.enabledFields && typeof safe.enabledFields === "object"
          ? safe.enabledFields
          : {}),
      });
      setBrandingOptions({
        ...emptyConfig.brandingOptions,
        ...(safe.brandingOptions && typeof safe.brandingOptions === "object"
          ? safe.brandingOptions
          : {}),
      });
      setPublishState(safe.publishState || "draft");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await hallTicketService.getHallTicketConfig(examId);
      const config = res?.data?.data ?? res?.data ?? null;
      if (config) applyConfig(config);
    } catch (err) {
      // A missing config (404) is a valid "not yet configured" state, not a hard error.
      const status = err?.response?.status;
      if (status && status !== 404) {
        setLoadError(parseApiError(err, "Failed to load configuration"));
      }
    } finally {
      setLoading(false);
    }
  }, [examId, applyConfig]);

  const loadRecords = useCallback(async () => {
    setRecordsLoading(true);
    try {
      const res = await hallTicketService.listHallTicketRecords(examId);
      const raw = res?.data?.data ?? res?.data ?? [];
      const list = Array.isArray(raw) ? raw : raw?.records ?? [];
      setRecords(Array.isArray(list) ? list : []);
    } catch (err) {
      // Non-fatal: records may not exist yet.
      setRecords([]);
    } finally {
      setRecordsLoading(false);
    }
  }, [examId]);

  useEffect(() => {
    if (!examId) {
      setLoadError("No exam selected");
      setLoading(false);
      return;
    }
    loadConfig();
    loadRecords();
  }, [examId, loadConfig, loadRecords]);

  const toggleField = (key) => {
    setEnabledFields((prev) => ({ ...prev, [key]: !prev?.[key] }));
  };

  const toggleBranding = (key) => {
    setBrandingOptions((prev) => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerateSummary(null);
    try {
      const res = await hallTicketService.generateHallTickets(examId);
      const summary = res?.data?.data ?? res?.data ?? null;
      setGenerateSummary(summary);
      const created = Number(summary?.created ?? 0);
      const updated = Number(summary?.updated ?? 0);
      toast.success(
        `Hall tickets generated (created ${created}, updated ${updated})`
      );
      await loadRecords();
    } catch (err) {
      toast.error(parseApiError(err, "Failed to generate hall tickets"));
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await hallTicketService.publishHallTickets(examId);
      const config = res?.data?.data ?? res?.data ?? {};
      setPublishState(config?.publishState || "published");
      toast.success("Hall tickets published");
    } catch (err) {
      toast.error(parseApiError(err, "Failed to publish hall tickets"));
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    setPublishing(true);
    try {
      const res = await hallTicketService.unpublishHallTickets(examId);
      const config = res?.data?.data ?? res?.data ?? {};
      setPublishState(config?.publishState || "unpublished");
      toast.success("Hall tickets unpublished");
    } catch (err) {
      toast.error(parseApiError(err, "Failed to unpublish hall tickets"));
    } finally {
      setPublishing(false);
    }
  };

  // Open the generated hall ticket PDF in a new tab for staff review. Resolves a
  // short-lived signed URL on demand (the records list stores only the blob key).
  const handlePreviewPdf = async (record) => {
    const recordId = record?.id;
    if (!recordId) {
      toast.error("This record cannot be previewed yet.");
      return;
    }
    // Pre-open a tab synchronously so the popup is not blocked, then navigate it
    // to the resolved signed URL (or close it on failure).
    const previewTab = window.open("", "_blank", "noopener,noreferrer");
    setPreviewingId(recordId);
    try {
      const res = await hallTicketService.getRecordPdfUrl(examId, recordId);
      const url = res?.data?.data?.pdfUrl ?? res?.data?.pdfUrl ?? null;
      if (!url) {
        if (previewTab) previewTab.close();
        toast.error("No PDF is available for this record.");
        return;
      }
      if (previewTab) {
        previewTab.location.href = url;
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      if (previewTab) previewTab.close();
      toast.error(parseApiError(err, "Failed to open hall ticket PDF"));
    } finally {
      setPreviewingId(null);
    }
  };

  const stateChip = useMemo(() => publishStateChip(publishState), [publishState]);
  const hasRecords = records.length > 0;
  const isPublished = String(publishState).toLowerCase() === "published";

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "40vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            size="small"
            sx={{ mb: 1 }}
          >
            Back
          </Button>
          <Typography variant="h5" fontWeight={600}>
            Hall Ticket Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure, generate and publish hall tickets for this exam.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={stateChip.label}
            color={stateChip.color}
            variant={stateChip.color === "default" ? "outlined" : "filled"}
          />
        </Stack>
      </Stack>

      {loadError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {loadError}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Configure */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardHeader
              title="Configuration"
              subheader="Content fields, instruction and center assignment"
            />
            <CardContent>
              <form onSubmit={formik.handleSubmit} noValidate>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Content Fields
                </Typography>
                <Grid container spacing={0}>
                  {CONTENT_FIELDS.map((f) => (
                    <Grid item xs={12} sm={6} key={f.key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!enabledFields?.[f.key]}
                            onChange={() => toggleField(f.key)}
                          />
                        }
                        label={f.label}
                      />
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Center Assignment
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      id="examCenter"
                      name="examCenter"
                      label="Exam Center"
                      value={formik.values.examCenter}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.examCenter &&
                        Boolean(formik.errors.examCenter)
                      }
                      helperText={
                        formik.touched.examCenter && formik.errors.examCenter
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      id="examRoom"
                      name="examRoom"
                      label="Exam Room"
                      value={formik.values.examRoom}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.examRoom &&
                        Boolean(formik.errors.examRoom)
                      }
                      helperText={
                        formik.touched.examRoom && formik.errors.examRoom
                      }
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Instruction
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  id="instruction"
                  name="instruction"
                  label="Instruction for students (optional)"
                  value={formik.values.instruction}
                  onChange={(e) => {
                    const next = e.target.value.slice(0, INSTRUCTION_MAX);
                    formik.setFieldValue("instruction", next, false);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.instruction &&
                    Boolean(formik.errors.instruction)
                  }
                  helperText={
                    (formik.touched.instruction && formik.errors.instruction) ||
                    `${(formik.values.instruction || "").length}/${INSTRUCTION_MAX}`
                  }
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Branding
                </Typography>
                <Grid container spacing={0}>
                  {BRANDING_FIELDS.map((f) => (
                    <Grid item xs={12} sm={6} key={f.key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!brandingOptions?.[f.key]}
                            onChange={() => toggleBranding(f.key)}
                          />
                        }
                        label={f.label}
                      />
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <PermissionGate permission={HALL_TICKET_PERMISSIONS.CONFIGURE}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        saving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <Save />
                        )
                      }
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save Configuration"}
                    </Button>
                  </PermissionGate>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Generate + Publish */}
        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 3 }}>
            <CardHeader
              title="Generate"
              subheader="Create hall tickets for enrolled students"
            />
            <CardContent>
              <PermissionGate
                permission={HALL_TICKET_PERMISSIONS.GENERATE}
                fallback={
                  <Typography variant="body2" color="text.secondary">
                    You do not have permission to generate hall tickets.
                  </Typography>
                }
              >
                <Button
                  variant="outlined"
                  startIcon={
                    generating ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <AutoFixHigh />
                    )
                  }
                  onClick={handleGenerate}
                  disabled={generating}
                >
                  {generating ? "Generating..." : "Generate Hall Tickets"}
                </Button>
              </PermissionGate>

              {generateSummary && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Created {Number(generateSummary.created ?? 0)}, updated{" "}
                  {Number(generateSummary.updated ?? 0)}, skipped{" "}
                  {Array.isArray(generateSummary.skipped)
                    ? generateSummary.skipped.length
                    : 0}
                  , PDF failed{" "}
                  {Array.isArray(generateSummary.pdfFailed)
                    ? generateSummary.pdfFailed.length
                    : 0}
                  .
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader
              title="Publish"
              subheader="Make hall tickets downloadable to students"
            />
            <CardContent>
              <PermissionGate
                permission={HALL_TICKET_PERMISSIONS.PUBLISH}
                fallback={
                  <Typography variant="body2" color="text.secondary">
                    You do not have permission to publish hall tickets.
                  </Typography>
                }
              >
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<Publish />}
                    onClick={handlePublish}
                    disabled={publishing || isPublished || !hasRecords}
                  >
                    Publish
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<Unpublished />}
                    onClick={handleUnpublish}
                    disabled={publishing || !isPublished}
                  >
                    Unpublish
                  </Button>
                </Stack>
                {!hasRecords && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 1 }}
                  >
                    Generate hall tickets before publishing.
                  </Typography>
                )}
              </PermissionGate>
            </CardContent>
          </Card>
        </Grid>

        {/* Records */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Generated Records"
              action={
                <Button
                  size="small"
                  startIcon={<Refresh />}
                  onClick={loadRecords}
                  disabled={recordsLoading}
                >
                  Refresh
                </Button>
              }
            />
            <CardContent>
              {recordsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : records.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No hall ticket records generated yet.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Student ID</TableCell>
                        <TableCell>Roll Number</TableCell>
                        <TableCell>Seat Number</TableCell>
                        <TableCell>PRN</TableCell>
                        <TableCell>Center</TableCell>
                        <TableCell>Room</TableCell>
                        <TableCell>PDF</TableCell>
                        <TableCell align="center">Preview</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {records.map((r) => (
                        <TableRow key={r?.id ?? `${r?.studentId}-${r?.rollNumber}`}>
                          <TableCell>{r?.studentId ?? "-"}</TableCell>
                          <TableCell>{r?.rollNumber ?? "-"}</TableCell>
                          <TableCell>{r?.seatNumber ?? "-"}</TableCell>
                          <TableCell>{r?.prnNumber ?? "-"}</TableCell>
                          <TableCell>{r?.examCenter ?? "-"}</TableCell>
                          <TableCell>{r?.examRoom ?? "-"}</TableCell>
                          <TableCell>
                            {r?.pdfUrl ? (
                              <Chip size="small" color="success" label="Ready" />
                            ) : (
                              <Chip size="small" label="Pending" />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <PermissionGate
                              permission={HALL_TICKET_PERMISSIONS.GENERATE}
                            >
                              <Button
                                size="small"
                                startIcon={
                                  previewingId === r?.id ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : (
                                    <Visibility fontSize="small" />
                                  )
                                }
                                onClick={() => handlePreviewPdf(r)}
                                disabled={!r?.pdfUrl || previewingId === r?.id}
                              >
                                {previewingId === r?.id ? "Opening..." : "Preview"}
                              </Button>
                            </PermissionGate>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HallTicketManagement;
